/**
 * Created by chachora on 11/26/18.
 */
import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from 'axios';
import { SlotEvent, Contact, Airport, RampService, User } from '../model/types';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import store from '../store';
import { RootState } from '../store/reducers';
import AuthHelper from '../helpers/auth-helper';
import * as authActions from '../store/auth.actions';
import {
  ACTIVITY_START,
  ACTIVITY_END,
  reportSlowNetwork,
  reportFastNetwork,
} from '../store/loading.actions';

const getThunkDispatch = () =>
  store.dispatch as ThunkDispatch<RootState, unknown, AnyAction>;

type ConfigWithMetadata = InternalAxiosRequestConfig & {
  _metadata?: { startTime: number };
};

const SLOW_REQUEST_THRESHOLD_MS = 5000;

export const STATION_TOOLS_API_URL =
  process.env.REACT_APP_STATION_TOOLS_API_URL;
export const STATION_TOOLS_API_URL_V2 =
  process.env.REACT_APP_STATION_TOOLS_API_URL_V2;

export const ax: AxiosInstance = axios.create({
  baseURL: STATION_TOOLS_API_URL,
});

export const axV2: AxiosInstance = axios.create({
  baseURL: STATION_TOOLS_API_URL_V2,
});

type RequestOptions = Pick<AxiosRequestConfig, 'signal'>;

function setApiInterceptors(axiosApi: AxiosInstance): void {
  axiosApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    store.dispatch({ type: ACTIVITY_START });

    config.headers.Authorization = AuthHelper.getToken();
    (config as ConfigWithMetadata)._metadata = { startTime: Date.now() };
    return config;
  });

  axiosApi.interceptors.response.use(
    res => {
      store.dispatch({ type: ACTIVITY_END });

      // Request-timing fallback for slow-network detection.
      // The Network Information API (navigator.connection.effectiveType) is
      // RTT-dominant and may still report "4g" on low-bandwidth connections.
      // Measuring actual request duration catches those cases.
      const startTime = (res.config as ConfigWithMetadata)._metadata?.startTime;
      if (startTime) {
        const duration = Date.now() - startTime;
        getThunkDispatch()(
          duration >= SLOW_REQUEST_THRESHOLD_MS
            ? reportSlowNetwork()
            : reportFastNetwork()
        );
      }

      return res;
    },
    (err: AxiosError) => {
      store.dispatch({ type: ACTIVITY_END });

      const startTime = (err.config as ConfigWithMetadata | undefined)
        ?._metadata?.startTime;
      if (startTime) {
        const duration = Date.now() - startTime;
        getThunkDispatch()(
          duration >= SLOW_REQUEST_THRESHOLD_MS
            ? reportSlowNetwork()
            : reportFastNetwork()
        );
      }

      if (axios.isCancel(err)) {
        return Promise.reject(err);
      }

      if (err.response && [401, 404].includes(err.response.status)) {
        authActions.resetAppState(store.dispatch);
      }

      return Promise.reject(err);
    }
  );
}

export const configureApi = (): void => {
  setApiInterceptors(ax);
  setApiInterceptors(axV2);
};

// TODO API should handle null or undefined values, when request is fails

type SlotData = {
  slot: unknown;
};

export const addSlot = async (slot: unknown): Promise<void> => {
  try {
    await ax.post<void>('/slots', { slot } as SlotData);
  } catch (err) {
    throw err;
  }
};

export const generateSlots = async (events: unknown): Promise<unknown> => {
  try {
    const res = await ax.post<unknown>('/slots/generate', events);
    return res.data;
  } catch (err) {
    throw err;
  }
};

type DateRange = {
  start: string;
  end: string;
};

export const getEvents = async (
  range: DateRange,
  options?: RequestOptions
): Promise<SlotEvent[] | undefined> => {
  try {
    const res = await ax.get<SlotEvent[]>('/events', {
      params: { ...range },
      signal: options?.signal,
    });
    return res.data;
  } catch (err) {
    if (axios.isCancel(err)) {
      return;
    }
    throw err;
  }
};

export const updateEventV2 = async (
  id: string,
  data: Partial<SlotEvent>
): Promise<SlotEvent> => {
  try {
    const res = await axV2.post<SlotEvent>(`/events/${id}`, data);
    return res.data;
  } catch (err) {
    throw err;
  }
};

export const deleteEvent = async (id: string): Promise<void> => {
  try {
    await ax.delete<void>(`/events/${id}`);
  } catch (err) {
    throw err;
  }
};

type EventReport = unknown;

export const submitEventReport = async (
  id: string,
  report: EventReport
): Promise<SlotEvent> => {
  try {
    const res = await ax.post<SlotEvent>(`/events/${id}/report`, report);
    return res.data;
  } catch (err) {
    throw err;
  }
};

export const getAirports = async (): Promise<Airport[]> => {
  try {
    const res = await ax.get<Airport[]>('/airports');
    return res.data;
  } catch (err) {
    throw err;
  }
};

export const getContacts = async (): Promise<Contact[]> => {
  try {
    const res = await ax.get<Contact[]>('/contacts');
    return res.data;
  } catch (err) {
    throw err;
  }
};

export const addContact = async (data: Partial<Contact>): Promise<Contact> => {
  try {
    const res = await ax.post<Contact>('/contacts', data);
    return res.data;
  } catch (err) {
    throw err;
  }
};

export const deleteContact = async (id: string): Promise<void> => {
  try {
    await ax.delete<void>(`/contacts/${id}`);
  } catch (err) {
    throw err;
  }
};

export const login = async (
  username: string,
  password: string
): Promise<AxiosResponse<{ authToken: string }>> => {
  try {
    return await ax.post<{ authToken: string }>('/login', {
      username,
      password,
    });
  } catch (err) {
    throw err;
  }
};

export const logout = async (): Promise<AxiosResponse<void>> => {
  try {
    return await ax.post<void>('/logout');
  } catch (err) {
    throw err;
  }
};

export const getRampServices = async (): Promise<RampService[]> => {
  try {
    const res = await ax.get<RampService[]>('/ramp-services');
    return res.data;
  } catch (err) {
    throw err;
  }
};

export const getUsers = async (): Promise<User[]> => {
  try {
    const res = await ax.get<User[]>('/users');
    return res.data;
  } catch (err) {
    throw err;
  }
};

// TODO: Admin user management (create/edit/delete/reset-password/status) needs
// API endpoints that do not exist yet. Only a read-only GET /users survives on
// the restored API — see UsersList.tsx.
